<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner />
    </div>
    <template v-else-if="checklist">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between flex-wrap">
          <div>
            <h3>{{ periodLabel }}</h3>
            <b-badge :variant="statusBadgeVariant(statusValue)">
              {{ enumLabel(period && period.status, $i18n.locale) }}
            </b-badge>
            <b-badge
              v-if="period && period.close_forced"
              variant="warning"
              class="ml-50"
            >
              {{ $t('ledger_close_forced') }}
            </b-badge>
          </div>
          <div class="d-none d-md-flex">
            <b-button
              v-if="canNormalClose"
              variant="danger"
              class="rounded-pill mr-50"
              :disabled="acting"
              @click="confirmClose(false)"
            >
              {{ $t('ledger_close_period') }}
            </b-button>
            <b-button
              v-if="canForce"
              variant="warning"
              class="rounded-pill mr-50"
              @click="forceOpen = true"
            >
              {{ $t('ledger_close_force') }}
            </b-button>
            <b-button
              v-if="canReopen"
              v-b-tooltip.hover
              variant="outline-primary"
              class="rounded-pill"
              :disabled="!(period && period.can_reopen)"
              :title="period.can_reopen ? '' : $t('ledger_close_reopen_blocked')"
              @click="reopenOpen = true"
            >
              {{ $t('ledger_reopen_period') }}
            </b-button>
          </div>
        </div>
        <p
          v-if="period && period.close_reason"
          class="mt-1 mb-0"
        >
          {{ period.close_reason }}
        </p>
      </b-card>

      <b-card class="rounded-lg">
        <b-tabs>
          <b-tab :title="$t('ledger_close_checklist')">
            <article
              v-for="item in checklist.items || []"
              :key="item.key"
              class="ledger-check-row"
            >
              <div class="d-flex justify-content-between">
                <div>
                  <span :class="markClass(item)">{{ mark(item) }}</span>
                  {{ item.label_ar || item.label }}
                  <span class="text-muted">({{ item.count }})</span>
                </div>
                <b-link
                  v-if="itemLink(item)"
                  :to="itemLink(item)"
                >
                  {{ $t('ledger_journal_open') }}
                </b-link>
              </div>
              <div
                v-if="item.key === 'earlier_periods_closed'"
                class="small"
              >
                <b-link
                  v-for="openPeriod in openPeriods(item)"
                  :key="openPeriod.id || openPeriod.period_key"
                  class="mr-1"
                  :to="{ name: 'ledger-periods-view', params: { id: openPeriod.id } }"
                >
                  {{ openPeriod.period_key || openPeriod.month }}
                </b-link>
              </div>
              <div
                v-if="item.details && (item.details.amount_due || item.details.revenue_due || item.details.debit)"
                class="small ledger-num"
              >
                <span v-if="item.details.amount_due">{{ money(item.details.amount_due) }}</span>
                <span v-if="item.details.revenue_due">{{ money(item.details.revenue_due) }}</span>
                <span v-if="item.details.debit != null">{{ money(item.details.debit) }} / {{ money(item.details.credit) }}</span>
              </div>
            </article>
          </b-tab>
          <b-tab
            :title="$t('ledger_close_history')"
            @click="loadEvents"
          >
            <article
              v-for="event in events"
              :key="event.id"
              class="ledger-check-row"
            >
              <div>
                <b-badge>{{ enumLabel(event.action, $i18n.locale) }}</b-badge>
                {{ event.user && event.user.name }}
              </div>
              <div class="text-muted small">
                {{ event.created_at }}
              </div>
              <div v-if="event.reason">
                {{ event.reason }}
              </div>
            </article>
          </b-tab>
        </b-tabs>
      </b-card>
    </template>

    <b-modal
      v-model="forceOpen"
      :title="$t('ledger_close_force')"
      hide-footer
    >
      <p>{{ $t('ledger_close_force_hint') }}</p>
      <ul>
        <li
          v-for="item in skippedItems"
          :key="item.key"
        >
          {{ item.label_ar || item.label }}
        </li>
      </ul>
      <b-form-textarea
        v-model="reason"
        rows="3"
      />
      <small
        v-if="reason.trim().length < 10"
        class="text-danger"
      >{{ $t('ledger_close_reason_min') }}</small>
      <b-button
        class="mt-1"
        variant="warning"
        :disabled="acting || reason.trim().length < 10"
        @click="confirmClose(true)"
      >
        {{ $t('ledger_close_force') }}
      </b-button>
    </b-modal>
    <b-modal
      v-model="reopenOpen"
      :title="$t('ledger_reopen_period')"
      hide-footer
    >
      <b-form-textarea
        v-model="reopenReason"
        rows="3"
      />
      <small
        v-if="reopenReason.trim().length < 10"
        class="text-danger"
      >{{ $t('ledger_close_reason_min') }}</small>
      <b-button
        class="mt-1"
        variant="primary"
        :disabled="acting || reopenReason.trim().length < 10"
        @click="submitReopen"
      >
        {{ $t('ledger_reopen_period') }}
      </b-button>
    </b-modal>
    <div
      v-if="checklist"
      class="ledger-close-sticky d-md-none"
    >
      <b-button
        v-if="canNormalClose"
        size="sm"
        variant="danger"
        @click="confirmClose(false)"
      >
        {{ $t('ledger_close_period') }}
      </b-button>
      <b-button
        v-if="canForce"
        size="sm"
        variant="warning"
        @click="forceOpen = true"
      >
        {{ $t('ledger_close_force') }}
      </b-button>
      <b-button
        v-if="canReopen"
        size="sm"
        variant="outline-primary"
        :disabled="!(period && period.can_reopen)"
        @click="reopenOpen = true"
      >
        {{ $t('ledger_reopen_period') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BCard, BBadge, BButton, BSpinner, BTabs, BTab, BLink, BModal, BFormTextarea, VBTooltip,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  getPeriodChecklist, closeAccountingPeriod, reopenAccountingPeriod, getPeriodEvents, getAccountingPeriods,
} from '@/api/accounting/accountingPeriods'
import {
  oneFrom, listFrom, enumValue, enumLabel, statusBadgeVariant, formatLedgerMoney, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'AccountingPeriodShow',
  components: {
    BCard, BBadge, BButton, BSpinner, BTabs, BTab, BLink, BModal, BFormTextarea,
  },
  directives: { 'b-tooltip': VBTooltip },
  data() {
    return {
      loading: false,
      acting: false,
      checklist: null,
      period: null,
      events: [],
      forceOpen: false,
      reopenOpen: false,
      reason: '',
      reopenReason: '',
    }
  },
  computed: {
    statusValue() {
      const fromPeriod = this.period && this.period.status
      const fromChecklist = this.checklist && this.checklist.period && this.checklist.period.status
      return enumValue(fromPeriod) || enumValue(fromChecklist)
    },
    periodLabel() {
      const period = this.checklist.period || this.period || {}
      return period.period_key || `${period.year}-${period.month}`
    },
    canNormalClose() {
      return this.checklist.can_close && this.hasPermission('close accounting_periods') && this.statusValue !== 'closed'
    },
    canForce() {
      return this.checklist.can_force_close
        && this.hasPermission('close accounting_periods')
        && this.hasPermission('force close accounting_periods')
        && this.statusValue !== 'closed'
    },
    canReopen() {
      return this.statusValue === 'closed' && this.hasPermission('reopen accounting_periods')
    },
    skippedItems() {
      return (this.checklist.items || []).filter(item => item.status === 'missing' && item.forceable)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumLabel,
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
    mark(item) {
      if (item.status === 'ok') return '✓'
      if (item.status === 'warning') return '!'
      if (item.status === 'not_required') return '—'
      return '✗'
    },
    markClass(item) {
      if (item.status === 'ok') return 'text-success'
      if (item.status === 'not_required') return 'text-muted'
      if (item.status === 'missing' && item.blocking) return 'text-danger'
      return 'text-warning'
    },
    openPeriods(item) {
      const rows = item.details && item.details.open_periods
      return Array.isArray(rows) ? rows : []
    },
    itemLink(item) {
      const period = this.checklist.period || {}
      const from = period.starts_on
      const to = period.ends_on
      const links = {
        draft_journals: { name: 'ledger-journals', query: { status: 'draft', date_from: from, date_to: to } },
        posting_failures: { name: 'ledger-posting-failures' },
        depreciation: { name: 'ledger-depreciation' },
        contract_recognition: { name: 'ledger-contract-recognition' },
        payroll: item.details && item.details.run_id
          ? { name: 'ledger-payroll-view', params: { id: item.details.run_id } }
          : { name: 'ledger-payroll' },
        trial_balance: { name: 'ledger-trial-balance', query: { as_of: to } },
        revenue_accruals: { name: 'ledger-revenue-accruals' },
        draft_invoices: { name: 'ledger-invoices', query: { status: 'draft' } },
        late_postings: { name: 'ledger-journals', query: { date_from: from, date_to: to } },
      }
      return links[item.key] || null
    },
    applyChecklist(payload) {
      const data = payload && payload.items ? payload : oneFrom(payload)
      this.checklist = data
      if (data && data.period) this.period = { ...(this.period || {}), ...data.period }
    },
    async load() {
      this.loading = true
      try {
        const res = await getPeriodChecklist(this.$route.params.id)
        this.applyChecklist(res)
        const year = this.checklist && this.checklist.period && this.checklist.period.year
        if (year) {
          const listed = listFrom(await getAccountingPeriods({ year }))
          const match = listed.find(row => String(row.id) === String(this.$route.params.id))
          if (match) this.period = { ...(this.period || {}), ...match }
        }
        await this.loadEvents()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async loadEvents() {
      try {
        const res = await getPeriodEvents(this.$route.params.id)
        this.events = listFrom(res)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      }
    },
    async confirmClose(force) {
      if (!force) {
        const result = await Swal.fire({
          title: this.$t('ledger_close_period'),
          text: this.$t('ledger_close_confirm'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('ledger_close_period'),
          cancelButtonText: this.$t('ledger_cancel'),
          customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
          buttonsStyling: false,
        })
        if (!result.isConfirmed) return
      }
      this.acting = true
      try {
        const body = force ? { force: true, reason: this.reason.trim() } : {}
        const res = await closeAccountingPeriod(this.$route.params.id, body)
        this.period = oneFrom(res)
        this.forceOpen = false
        this.reason = ''
        this.toast(this.$t('ledger_period_closed_ok'), 'success')
        await this.load()
        await this.loadEvents()
      } catch (error) {
        const payload = error?.response?.data
        if (payload && payload.data && payload.data.items) this.applyChecklist(payload.data)
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.acting = false
      }
    },
    async submitReopen() {
      this.acting = true
      try {
        const res = await reopenAccountingPeriod(this.$route.params.id, { reason: this.reopenReason.trim() })
        this.period = oneFrom(res)
        this.reopenOpen = false
        this.reopenReason = ''
        this.toast(this.$t('ledger_period_reopened'), 'success')
        await this.load()
        await this.loadEvents()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.acting = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-check-row { border-bottom: 1px solid #eee; padding: 0.75rem 0; }
.ledger-close-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex; gap: 0.5rem;
  background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
